/** The contacts list field (plan "Contacts card (D6)") — pure.
 *
 *  A person's email addresses, phone numbers and URLs as ONE list-valued draft field, plus
 *  which rows are the user's own email and phone (there is no primary column: `user_email`
 *  / `user_phone` name the rows). The wire value (UserRecordContacts) is the whole list in
 *  order — by type (email, phone, URL), then the order within a type — and the draft holds
 *  the whole value; this module is every edit the widget makes to it, the row states the
 *  widget shows against the baseline (kept / added / changed / removed), the rules the
 *  server enforces that the widget can say up front, the ledger's lines (each with its own
 *  revert), and the divergence between the account and its contacts.
 *
 *  Rules (the server's, `user_changeset/contacts.py`):
 *  - Editing a value resets its verified flag (O9); typing the stored value back restores
 *    the stored flag, so the change disappears.
 *  - Removing or disabling the contact that is the user email / phone keeps `user_email` /
 *    `user_phone` on it: the server then asks which contact takes over (the choice
 *    question) — unless the operator makes another row the user's. A removed user phone
 *    with no other enabled phone left says "User phone → none" (`user_phone: null`)
 *    instead; settled after every edit (`settleUserPhone`), so removing the old phone and
 *    adding a new one asks the question in either order. The only enabled email contact
 *    that is the user email can be neither removed nor disabled.
 *  - Moving the user email / phone to another contact freezes it (O10) — the server sets
 *    the freeze; the ledger says so.
 *  - An added or edited value is checked (email format here; a phone is normalised by the
 *    preview) and may not repeat another contact of its type; a stored value left as it is
 *    is never re-checked. */
import { draftValuesEqual, type RecordChangeset } from '../../draft/changeSet';
import type { RecordWidgetProtocol } from '../../editor/types';

export type ContactType = 'email' | 'phone' | 'url';
/** `auto` (a draft value only) — the importer's heuristics decide on save. */
export type ContactTier = 'private' | 'ota' | 'fake' | 'auto';
export type UserAddressType = 'email' | 'phone';

export const CONTACT_TYPES: readonly ContactType[] = ['email', 'phone', 'url'];
export const CONTACT_TIERS: readonly ContactTier[] = ['auto', 'private', 'ota', 'fake'];

export const TYPE_LABEL: Record<ContactType, string> = { email: 'Email', phone: 'Phone', url: 'URL' };
export const TIER_LABEL: Record<ContactTier, string> = { auto: 'Auto', private: 'Private', ota: 'OTA', fake: 'Fake' };

export interface ContactRow {
  /** `"contact:<id>"` for a stored row; `"new:<n>"` for one the draft adds. */
  key: string;
  id: number | null;
  type: ContactType;
  contact: string;
  tier: ContactTier;
  verified: boolean;
  /** Notifications allowed to this contact. */
  enabled: boolean;
}

export interface ContactsValue {
  contacts: ContactRow[];
  user_email: string | null;
  user_phone: string | null;
}

/** The account's own email and phone (users.User), for the divergence rows. A verified
 *  flag the record does not carry is null (a booking's guest has no phone_verified): no
 *  mismatch is noted, and an "Add as contact" adds it unverified. */
export interface ContactsAccount {
  email: string | null;
  phone: string | null;
  email_verified: boolean | null;
  phone_verified: boolean | null;
}

export const EMPTY_CONTACTS: ContactsValue = { contacts: [], user_email: null, user_phone: null };

// ---------------------------------------------------------------------------
// Shape
// ---------------------------------------------------------------------------

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function asType(v: unknown): ContactType {
  return v === 'phone' || v === 'url' ? v : 'email';
}

function asTier(v: unknown): ContactTier {
  return v === 'ota' || v === 'fake' || v === 'auto' ? v : 'private';
}

/** One row with a fixed key order (the server's), whatever it was built from. */
function row(r: ContactRow): ContactRow {
  return {
    key: r.key,
    id: r.id,
    type: r.type,
    contact: r.contact,
    tier: r.tier,
    verified: r.verified,
    enabled: r.enabled,
  };
}

function value(contacts: ContactRow[], userEmail: string | null, userPhone: string | null): ContactsValue {
  return { contacts: sortByType(contacts), user_email: userEmail, user_phone: userPhone };
}

/** A wire value (a baseline, a draft, a 409's `current`) as a well-formed value. */
export function asContactsValue(v: unknown): ContactsValue {
  if (!isObject(v) || !Array.isArray(v.contacts)) return EMPTY_CONTACTS;
  const contacts = v.contacts.filter(isObject).map((r) =>
    row({
      key: typeof r.key === 'string' ? r.key : `contact:${String(r.id)}`,
      id: typeof r.id === 'number' ? r.id : null,
      type: asType(r.type),
      contact: typeof r.contact === 'string' ? r.contact : '',
      tier: asTier(r.tier),
      verified: r.verified === true,
      enabled: r.enabled !== false,
    }),
  );
  return {
    contacts,
    user_email: typeof v.user_email === 'string' ? v.user_email : null,
    user_phone: typeof v.user_phone === 'string' ? v.user_phone : null,
  };
}

/** Stable by type (email, phone, URL), keeping the order within a type. */
function sortByType(rows: ContactRow[]): ContactRow[] {
  return CONTACT_TYPES.flatMap((t) => rows.filter((r) => r.type === t));
}

function find(v: ContactsValue, key: string | null): ContactRow | undefined {
  return key === null ? undefined : v.contacts.find((r) => r.key === key);
}

// ---------------------------------------------------------------------------
// Addresses
// ---------------------------------------------------------------------------

/** A phone number's comparable form: its digits, with the leading plus. */
export function phoneDigits(v: string): string {
  const t = v.trim();
  return `${t.startsWith('+') ? '+' : ''}${t.replace(/\D/g, '')}`;
}

/** Same address, as the server compares them: email case-insensitively, a phone by its
 *  digits, a URL as text. */
export function sameAddress(type: ContactType, a: string | null | undefined, b: string | null | undefined): boolean {
  const x = (a ?? '').trim();
  const y = (b ?? '').trim();
  if (!x || !y) return false;
  if (type === 'email') return x.toLowerCase() === y.toLowerCase();
  if (type === 'phone') {
    const dx = phoneDigits(x);
    const dy = phoneDigits(y);
    // "+39 333 …" and "0039333…" or a number stored without its plus still match by tail.
    return dx === dy || dx.replace(/^\+/, '') === dy.replace(/^\+/, '');
  }
  return x === y;
}

/** The row key the user email / phone names. */
export function userAddressKey(v: ContactsValue, type: UserAddressType): string | null {
  return type === 'email' ? v.user_email : v.user_phone;
}

function withUserAddress(v: ContactsValue, type: UserAddressType, key: string | null): ContactsValue {
  return type === 'email' ? { ...v, user_email: key } : { ...v, user_phone: key };
}

// ---------------------------------------------------------------------------
// Row states (the widget against the baseline)
// ---------------------------------------------------------------------------

export type ContactRowState = 'kept' | 'added' | 'changed' | 'removed';

export interface ContactListItem {
  row: ContactRow;
  /** The stored row (null: added). */
  base: ContactRow | null;
  state: ContactRowState;
  valueChanged: boolean;
  verifiedChanged: boolean;
  enabledChanged: boolean;
  tierChanged: boolean;
}

function compare(base: ContactRow | null, r: ContactRow): Omit<ContactListItem, 'row' | 'base' | 'state'> {
  return {
    valueChanged: base !== null && base.contact !== r.contact,
    verifiedChanged: base !== null && base.verified !== r.verified,
    enabledChanged: base !== null && base.enabled !== r.enabled,
    tierChanged: base !== null && base.tier !== r.tier,
  };
}

/** The rows the widget shows: the draft's, each against its stored row, and the stored
 *  rows the draft removes (struck, restorable) at the end of their type. */
export function contactListItems(baseline: ContactsValue | null, v: ContactsValue): ContactListItem[] {
  const bases = new Map((baseline?.contacts ?? []).map((r) => [r.key, r]));
  const present = new Set(v.contacts.map((r) => r.key));
  const out: ContactListItem[] = [];
  for (const type of CONTACT_TYPES) {
    for (const r of v.contacts.filter((x) => x.type === type)) {
      const base = bases.get(r.key) ?? null;
      const diff = compare(base, r);
      const changed = diff.valueChanged || diff.verifiedChanged || diff.enabledChanged || diff.tierChanged;
      out.push({ row: r, base, state: base === null ? 'added' : changed ? 'changed' : 'kept', ...diff });
    }
    for (const r of baseline?.contacts.filter((x) => x.type === type && !present.has(x.key)) ?? []) {
      out.push({
        row: r,
        base: r,
        state: 'removed',
        valueChanged: false,
        verifiedChanged: false,
        enabledChanged: false,
        tierChanged: false,
      });
    }
  }
  return out;
}

/** Whether the type's order differs from the stored one (the rows both have). */
export function reordered(baseline: ContactsValue, v: ContactsValue, type: ContactType): boolean {
  const kept = new Set(v.contacts.map((r) => r.key));
  const before = baseline.contacts.filter((r) => r.type === type && kept.has(r.key)).map((r) => r.key);
  const stored = new Set(before);
  const after = v.contacts.filter((r) => r.type === type && stored.has(r.key)).map((r) => r.key);
  return before.join('|') !== after.join('|');
}

// ---------------------------------------------------------------------------
// Edits
// ---------------------------------------------------------------------------

/** A key no row of either value uses: `new:1`, `new:2`, … */
export function newContactKey(v: ContactsValue, baseline: ContactsValue | null = null): string {
  const used = new Set([...v.contacts, ...(baseline?.contacts ?? [])].map((r) => r.key));
  let n = 1;
  while (used.has(`new:${n}`)) n += 1;
  return `new:${n}`;
}

/** A new row at the end of its type: tier `auto` (the importer's heuristics), notifications
 *  on, unverified. `asUser`: it becomes the user email / phone (the "Add as contact" of an
 *  address that is not a contact yet). */
export function addContact(
  v: ContactsValue,
  type: ContactType,
  {
    contact = '',
    verified = false,
    asUser = false,
    baseline = null,
  }: { contact?: string; verified?: boolean; asUser?: boolean; baseline?: ContactsValue | null } = {},
): { value: ContactsValue; key: string } {
  const key = newContactKey(v, baseline);
  const added = row({ key, id: null, type, contact, tier: 'auto', verified, enabled: true });
  const next = value([...v.contacts, added], v.user_email, v.user_phone);
  return { value: settleUserPhone(asUser && type !== 'url' ? withUserAddress(next, type, key) : next, baseline), key };
}

function mapRow(v: ContactsValue, key: string, f: (r: ContactRow) => ContactRow): ContactsValue {
  return { ...v, contacts: v.contacts.map((r) => (r.key === key ? row(f(r)) : r)) };
}

/** A new value for a row — its verified flag resets (O9), unless the text is the stored
 *  value again, which takes the stored flag back. */
export function editContactValue(
  v: ContactsValue,
  key: string,
  text: string,
  baseline: ContactsValue | null,
): ContactsValue {
  const base = find(baseline ?? EMPTY_CONTACTS, key);
  const next = mapRow(v, key, (r) => ({
    ...r,
    contact: text,
    verified: base && base.contact === text ? base.verified : base ? false : r.verified,
  }));
  return settleUserPhone(next, baseline);
}

export function setContactVerified(v: ContactsValue, key: string, on: boolean): ContactsValue {
  return mapRow(v, key, (r) => ({ ...r, verified: on }));
}

export function setContactEnabled(
  v: ContactsValue,
  key: string,
  on: boolean,
  baseline: ContactsValue | null = null,
): ContactsValue {
  return settleUserPhone(
    mapRow(v, key, (r) => ({ ...r, enabled: on })),
    baseline,
  );
}

export function setContactTier(v: ContactsValue, key: string, tier: ContactTier): ContactsValue {
  return mapRow(v, key, (r) => ({ ...r, tier }));
}

/** Enabled rows of `type` other than `except`. */
function otherEnabled(v: ContactsValue, type: ContactType, except: string): ContactRow[] {
  return v.contacts.filter((r) => r.type === type && r.enabled && r.key !== except);
}

/** An enabled phone row other than `except` that has a number — one the server could make
 *  the user phone (an empty new row is not an answer yet). */
function hasOtherPhone(v: ContactsValue, except: string | null): boolean {
  return v.contacts.some((r) => r.type === 'phone' && r.enabled && r.key !== except && r.contact.trim() !== '');
}

/** The user phone once the stored user-phone row is removed, whatever order the edits came
 *  in (the server's rule, `contacts._pick`): while another enabled phone with a number is
 *  left, `user_phone` stays on the removed row — the server asks who takes over; with none
 *  left it is null — "User phone → none". A pointer the operator moved to another row is
 *  theirs; nothing to settle while the stored row is still in the list (or without a
 *  baseline). */
export function settleUserPhone(v: ContactsValue, baseline: ContactsValue | null): ContactsValue {
  const stored = baseline?.user_phone ?? null;
  if (stored === null || find(v, stored)) return v;
  if (v.user_phone !== null && v.user_phone !== stored) return v;
  const next = hasOtherPhone(v, stored) ? stored : null;
  return next === v.user_phone ? v : withUserAddress(v, 'phone', next);
}

/** Remove a row. A row the draft added just goes (and whatever pointed at it falls back
 *  to the stored choice). The user email / phone row keeps its pointer — the server asks
 *  who takes over — except the only phone, which leaves the user without one. */
export function removeContact(v: ContactsValue, key: string, baseline: ContactsValue | null = null): ContactsValue {
  const r = find(v, key);
  if (!r) return v;
  let next: ContactsValue = { ...v, contacts: v.contacts.filter((x) => x.key !== key) };
  for (const type of ['email', 'phone'] as const) {
    if (userAddressKey(next, type) !== key) continue;
    if (r.id === null) {
      // An added row was never the stored user address: back to the stored one.
      next = withUserAddress(next, type, baseline ? userAddressKey(baseline, type) : null);
    } else if (type === 'phone' && !hasOtherPhone(v, key)) {
      next = withUserAddress(next, type, null);
    }
  }
  return settleUserPhone(next, baseline);
}

/** Put a removed stored row back (at the end of its type) — and, when it was the stored
 *  user email / phone and still is the pointer's stored value, its role with it. */
export function restoreContact(v: ContactsValue, baseline: ContactsValue, key: string): ContactsValue {
  const base = find(baseline, key);
  if (!base || find(v, key)) return v;
  let next = value([...v.contacts, base], v.user_email, v.user_phone);
  for (const type of ['email', 'phone'] as const) {
    if (userAddressKey(baseline, type) === key && userAddressKey(next, type) === null) {
      next = withUserAddress(next, type, key);
    }
  }
  return next;
}

/** Back to the stored row: its value and flags (an added row goes; a removed one comes
 *  back). */
export function revertContact(v: ContactsValue, baseline: ContactsValue, key: string): ContactsValue {
  const base = find(baseline, key);
  if (!base) return removeContact(v, key, baseline);
  if (!find(v, key)) return restoreContact(v, baseline, key);
  return mapRow(v, key, () => base);
}

/** Move a row up (-1) or down (+1) among the rows of its type. */
export function moveContact(v: ContactsValue, key: string, dir: -1 | 1): ContactsValue {
  const r = find(v, key);
  if (!r) return v;
  const same = v.contacts.filter((x) => x.type === r.type);
  const i = same.findIndex((x) => x.key === key);
  const j = i + dir;
  if (j < 0 || j >= same.length) return v;
  const moved = [...same];
  [moved[i], moved[j]] = [moved[j]!, moved[i]!];
  return value(
    CONTACT_TYPES.flatMap((t) => (t === r.type ? moved : v.contacts.filter((x) => x.type === t))),
    v.user_email,
    v.user_phone,
  );
}

/** Make a row the user email / phone (its type decides which). */
export function makeUserAddress(v: ContactsValue, key: string): ContactsValue {
  const r = find(v, key);
  if (!r || r.type === 'url') return v;
  return withUserAddress(v, r.type, key);
}

/** Whether a row can be moving up / down. */
export function moveBounds(v: ContactsValue, key: string): { up: boolean; down: boolean } {
  const r = find(v, key);
  if (!r) return { up: false, down: false };
  const same = v.contacts.filter((x) => x.type === r.type);
  const i = same.findIndex((x) => x.key === key);
  return { up: i > 0, down: i < same.length - 1 };
}

// ---------------------------------------------------------------------------
// Rules the widget says up front
// ---------------------------------------------------------------------------

/** Why this row can be neither removed nor have its notifications turned off: the only
 *  enabled email contact, while it is the user email. */
export function keepReason(v: ContactsValue, key: string): string | null {
  const r = find(v, key);
  if (!r || r.type !== 'email' || v.user_email !== key || !r.enabled) return null;
  return otherEnabled(v, 'email', key).length === 0
    ? 'The user email needs an enabled email contact — add another one first'
    : null;
}

/** Whether the server will ask who becomes the user email / phone: its row is removed or
 *  its notifications disabled and `to` names no other enabled row (`user_changeset`
 *  `_pick`). */
export function userAddressAsked(baseline: ContactsValue | null, to: ContactsValue, type: UserAddressType): boolean {
  const stored = baseline ? userAddressKey(baseline, type) : null;
  const k = userAddressKey(to, type);
  if (k === null) return type === 'email' && stored !== null;
  const r = find(to, k);
  if (r && r.enabled) return false;
  return k === stored;
}

/** The answers of a raised "Which contact becomes the user email / phone?": the enabled
 *  rows of that type the draft keeps. */
export function userAddressAnswers(to: ContactsValue, type: UserAddressType): Array<{ value: string; label: string }> {
  const asked = userAddressKey(to, type);
  return to.contacts
    .filter((r) => r.type === type && r.enabled && r.key !== asked && r.contact.trim() !== '')
    .map((r) => ({ value: r.key, label: r.id === null ? `${r.contact} (new)` : r.contact }));
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface ContactProblem {
  key: string;
  message: string;
}

/** What is wrong with the rows the draft adds or edits (a stored value left as it is is
 *  never re-checked — the server keeps legacy values as they are). */
export function contactProblems(to: ContactsValue, from: ContactsValue | null): ContactProblem[] {
  const out: ContactProblem[] = [];
  for (const r of to.contacts) {
    const base = find(from ?? EMPTY_CONTACTS, r.key);
    if (base && base.contact === r.contact) continue;
    const text = r.contact.trim();
    if (!text) {
      out.push({ key: r.key, message: `Enter the ${r.type === 'url' ? 'URL' : TYPE_LABEL[r.type].toLowerCase()}` });
      continue;
    }
    if (r.type === 'email' && !EMAIL_RE.test(text)) {
      out.push({ key: r.key, message: 'Enter an email address' });
      continue;
    }
    if (to.contacts.some((x) => x.key !== r.key && x.type === r.type && sameAddress(r.type, x.contact, text))) {
      out.push({ key: r.key, message: `Another ${TYPE_LABEL[r.type].toLowerCase()} contact has this value` });
    }
  }
  return out;
}

/** The field-level summary of `contactProblems` (null: nothing to fix). */
export function contactsProblem(to: unknown, from: unknown): string | null {
  const n = contactProblems(asContactsValue(to), from === undefined ? null : asContactsValue(from)).length;
  if (n === 0) return null;
  return n === 1 ? 'Fix 1 contact' : `Fix ${n} contacts`;
}

// ---------------------------------------------------------------------------
// Display
// ---------------------------------------------------------------------------

/** "Email jane@example.com (user email) · Phone +1555… (user phone) · +1 more" — the
 *  value in one line (history labels, "Changed on the server to …"). */
export function contactsSummary(v: unknown, max = 3): string {
  const c = asContactsValue(v);
  if (c.contacts.length === 0) return 'No contacts';
  const mark = (r: ContactRow) =>
    r.key === c.user_email ? ' (user email)' : r.key === c.user_phone ? ' (user phone)' : r.enabled ? '' : ' (notifications off)';
  const parts = c.contacts.slice(0, max).map((r) => `${TYPE_LABEL[r.type]} ${r.contact || '(empty)'}${mark(r)}`);
  const more = c.contacts.length - max;
  return more > 0 ? `${parts.join(' · ')} · +${more} more` : parts.join(' · ');
}

function label(r: ContactRow): string {
  return `${TYPE_LABEL[r.type]} ${r.contact.trim() || '(empty)'}`;
}

// ---------------------------------------------------------------------------
// Ledger lines
// ---------------------------------------------------------------------------

export interface ContactsLedgerLine {
  /** Stable within one change. */
  id: string;
  text: string;
  /** The draft value with this line taken back. */
  revert: (to: ContactsValue) => ContactsValue;
}

/** The change in words, one line per thing it does ("Contact added · Email a@b.it",
 *  "User email → a@b.it (freezes it)"), each with its own revert. A user email / phone
 *  left on a removed or disabled row is the choice question's, not a line. */
export function contactsLedgerLines(fromRaw: unknown, toRaw: unknown): ContactsLedgerLine[] {
  const from = asContactsValue(fromRaw);
  const to = asContactsValue(toRaw);
  const lines: ContactsLedgerLine[] = [];
  for (const item of contactListItems(from, to)) {
    const r = item.row;
    const base = item.base;
    if (item.state === 'removed') {
      lines.push({ id: `removed:${r.key}`, text: `Contact removed · ${label(r)}`, revert: (v) => restoreContact(v, from, r.key) });
      continue;
    }
    if (item.state === 'added') {
      lines.push({ id: `added:${r.key}`, text: `Contact added · ${label(r)}`, revert: (v) => removeContact(v, r.key, from) });
      continue;
    }
    if (!base) continue;
    if (item.valueChanged) {
      const flag = item.verifiedChanged && !r.verified ? ' · unverified' : '';
      lines.push({
        id: `value:${r.key}`,
        text: `${TYPE_LABEL[r.type]} ${base.contact} → ${r.contact.trim() || '(empty)'}${flag}`,
        revert: (v) => mapRow(v, r.key, (x) => ({ ...x, contact: base.contact, verified: base.verified })),
      });
    } else if (item.verifiedChanged) {
      lines.push({
        id: `verified:${r.key}`,
        text: `${r.verified ? 'Verified' : 'Unverified'} · ${label(r)}`,
        revert: (v) => setContactVerified(v, r.key, base.verified),
      });
    }
    if (item.enabledChanged) {
      lines.push({
        id: `enabled:${r.key}`,
        text: `Notifications ${r.enabled ? 'on' : 'off'} · ${label(r)}`,
        revert: (v) => setContactEnabled(v, r.key, base.enabled, from),
      });
    }
    if (item.tierChanged) {
      lines.push({
        id: `tier:${r.key}`,
        text: `${label(r)} → ${TIER_LABEL[r.tier]}`,
        revert: (v) => setContactTier(v, r.key, base.tier),
      });
    }
  }
  for (const type of CONTACT_TYPES) {
    if (!reordered(from, to, type)) continue;
    lines.push({
      id: `order:${type}`,
      text: `Reordered the ${TYPE_LABEL[type].toLowerCase()} contacts`,
      revert: (v) => restoreOrder(v, from, type),
    });
  }
  for (const type of ['email', 'phone'] as const) {
    const before = userAddressKey(from, type);
    const after = userAddressKey(to, type);
    if (before === after || userAddressAsked(from, to, type)) continue;
    const target = find(to, after);
    const word = type === 'email' ? 'User email' : 'User phone';
    lines.push({
      id: `user:${type}`,
      text: target ? `${word} → ${target.contact.trim() || '(empty)'} (freezes it)` : `${word} → none`,
      revert: (v) => withUserAddress(v, type, before),
    });
  }
  return lines;
}

/** What the server's notes on a contacts change say beyond the ledger's own lines. The
 *  preview words every row change again ("adds Email a@b.it", "User email → a@b.it
 *  (frozen)", " · saved as +39…") — the lines already say those, each with its revert, and
 *  the row shows "Saved as …". Kept: whatever else it says — a contact added meanwhile
 *  ("keeps … (added since the editor loaded the user)"), an OTA relay address forced to
 *  OTA, the account's verified flag mirrored, and any note this list does not know. */
const ECHOED_NOTE = [
  /^adds /,
  /^changes /,
  /^removes /,
  /^(verifies|unverifies) /,
  /^(enables|disables) notifications to /,
  /^reorders the /,
  /^User (email|phone) → /,
  /^(Email|Phone|URL) .+ → (Private|OTA|Fake)$/,
  /^(verified|unverified)$/,
  /^saved as /,
];

export function contactsNoteExtras(notes: readonly string[]): string[] {
  const parts = notes.flatMap((n) => n.split(' · ')).map((p) => p.trim());
  return [...new Set(parts.filter((p) => p !== '' && !ECHOED_NOTE.some((re) => re.test(p))))];
}

/** A changeset's `to` as the preview should see it: rows the draft added and has not
 *  filled in yet are left out (an empty row is a row being typed, not an error to report),
 *  and a user email / phone naming one falls back to the stored choice. */
export function withoutEmptyAddedContacts(to: ContactsValue, from: ContactsValue | null): ContactsValue {
  const empty = new Set(to.contacts.filter((r) => r.id === null && r.contact.trim() === '').map((r) => r.key));
  if (empty.size === 0) return to;
  let next: ContactsValue = { ...to, contacts: to.contacts.filter((r) => !empty.has(r.key)) };
  for (const type of ['email', 'phone'] as const) {
    const k = userAddressKey(next, type);
    if (k !== null && empty.has(k)) next = withUserAddress(next, type, from ? userAddressKey(from, type) : null);
  }
  return settleUserPhone(next, from);
}

/** The contacts list field's key in a changeset (the user record's and the booking's). */
export const CONTACTS_FIELD = 'contacts';

/** What the contacts list's values mean to the editor and the ledger (the widget protocol
 *  of `ContactsWidget`): a summary for its "was …" and stale captions, the checks of the
 *  rows a change adds or edits, one ledger line per row change (each with its revert) with
 *  the preview's notes less what the lines say, and a list's ownership of its rows'
 *  errors, change marks and questions. */
export const CONTACTS_PROTOCOL: RecordWidgetProtocol = {
  formatChange: (value) => contactsSummary(value),
  checkValue: contactsProblem,
  ledgerLines: (from, to) =>
    contactsLedgerLines(from, to).map((l) => ({ id: l.id, text: l.text, revert: (v: unknown) => l.revert(asContactsValue(v)) })),
  ledgerNotes: contactsNoteExtras,
  owns: 'items',
};

/** What the preview is asked — on the user record and on the booking's Guest panel alike:
 *  the draft's changes, less the contacts rows still being typed (added, no value yet;
 *  `withoutEmptyAddedContacts`). A contacts change that is only such rows is left out, so
 *  a draft holding nothing else asks nothing (the editor reads that as settled). */
export function contactsPreviewBody(body: RecordChangeset | null): RecordChangeset | null {
  if (!body) return body;
  let stripped = false;
  const changes = body.changes.flatMap((c) => {
    if (c.field !== CONTACTS_FIELD) return [c];
    const raw = asContactsValue(c.to);
    const from = c.from === undefined ? null : asContactsValue(c.from);
    const to = withoutEmptyAddedContacts(raw, from);
    if (to === raw) return [c];
    stripped = true;
    return from && draftValuesEqual(to, from) ? [] : [{ ...c, to }];
  });
  return stripped ? { ...body, changes } : body;
}

/** The stored order of a type's stored rows, the added ones after them. */
function restoreOrder(v: ContactsValue, baseline: ContactsValue, type: ContactType): ContactsValue {
  const current = v.contacts.filter((r) => r.type === type);
  const keys = new Set(current.map((r) => r.key));
  const stored = baseline.contacts.filter((r) => r.type === type && keys.has(r.key)).map((r) => r.key);
  const byKey = new Map(current.map((r) => [r.key, r]));
  const ordered = [...stored.map((k) => byKey.get(k)!), ...current.filter((r) => !stored.includes(r.key))];
  return value(
    CONTACT_TYPES.flatMap((t) => (t === type ? ordered : v.contacts.filter((x) => x.type === t))),
    v.user_email,
    v.user_phone,
  );
}

// ---------------------------------------------------------------------------
// Divergence (plan: "Divergence is shown, not hidden")
// ---------------------------------------------------------------------------

export interface ContactsDivergence {
  /** The account's email / phone with no contact of that value. */
  missing: Array<{ type: UserAddressType; value: string; verified: boolean }>;
  /** Rows matching the account's address while another row does too. */
  duplicates: ReadonlySet<string>;
  /** Rows matching the account's address, by type. */
  matching: Readonly<Record<UserAddressType, ReadonlySet<string>>>;
  /** The user-address row whose verified flag disagrees with the account's. */
  verifiedMismatch: ReadonlyMap<string, { type: UserAddressType; account: boolean }>;
}

/** The user's email / phone as a draft `v` leaves it: the account's while neither the
 *  stored value nor the draft names a row (an address that is not a contact); the value
 *  of the row the draft names; none once the draft clears it ("User phone → none") or
 *  leaves it on a removed row (the choice question decides). Without a baseline (the
 *  stored list itself), the account's. */
function draftAddress(
  v: ContactsValue,
  account: ContactsAccount,
  baseline: ContactsValue | null,
  type: UserAddressType,
): string | null {
  const saved = type === 'email' ? account.email : account.phone;
  if (!baseline) return saved;
  const named = userAddressKey(v, type);
  if (named === null) return userAddressKey(baseline, type) === null ? saved : null;
  return find(v, named)?.contact ?? null;
}

/** The divergence between the account and the contacts `v`. With `baseline` (a draft), the
 *  account's address is the one the draft leaves (`draftAddress`), so a removed or edited
 *  user phone does not resurface as "not in contacts". */
export function contactsDivergence(
  v: ContactsValue,
  account: ContactsAccount | null | undefined,
  baseline: ContactsValue | null = null,
): ContactsDivergence {
  const missing: ContactsDivergence['missing'] = [];
  const duplicates = new Set<string>();
  const matching: Record<UserAddressType, Set<string>> = { email: new Set(), phone: new Set() };
  const verifiedMismatch = new Map<string, { type: UserAddressType; account: boolean }>();
  if (!account) return { missing, duplicates, matching, verifiedMismatch };
  for (const type of ['email', 'phone'] as const) {
    const saved = type === 'email' ? account.email : account.phone;
    const address = draftAddress(v, account, baseline, type);
    // The account's verified flag is the saved address's; another address has none yet.
    const accountVerified = sameAddress(type, address, saved)
      ? type === 'email'
        ? account.email_verified
        : account.phone_verified
      : null;
    if (!address?.trim()) continue;
    const rows = v.contacts.filter((r) => r.type === type && sameAddress(type, r.contact, address));
    for (const r of rows) matching[type].add(r.key);
    if (rows.length === 0) missing.push({ type, value: address.trim(), verified: accountVerified ?? false });
    if (rows.length > 1) for (const r of rows) duplicates.add(r.key);
    const user = find(v, userAddressKey(v, type));
    if (accountVerified !== null && user && sameAddress(type, user.contact, address) && user.verified !== accountVerified) {
      verifiedMismatch.set(user.key, { type, account: accountVerified });
    }
  }
  return { missing, duplicates, matching, verifiedMismatch };
}
